import { useState, useEffect } from "react";
import { authFetch } from "../lib/authFetch";
import { usePortailRouter } from "../lib/router-context";
import { usePortailAuth } from "../lib/auth-context";
import { Plus, Loader2, FileSignature, Eye, Pencil, Package, RefreshCw, ShoppingCart } from "lucide-react";

interface DevisItem {
  id: string;
  numero: string;
  statut: string;
  objet: string | null;
  clientFinalNom: string | null;
  lignes: any[];
  remiseGlobale: string | null;
  createdAt: string;
  typeDemande: string | null;
  environnement: string | null;
}

const TYPE_LABELS: Record<string, { label: string; className: string }> = {
  achat: { label: "Achat", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  location: { label: "Location", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" },
  mixte: { label: "Mixte", className: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" },
};

const STATUT_LABELS: Record<string, string> = {
  brouillon: "Brouillon",
  envoye: "Envoyé",
  accepte: "Accepté",
  refuse: "Refusé",
  expire: "Expiré",
};

const STATUT_CLASSES: Record<string, string> = {
  brouillon: "bg-muted text-muted-foreground",
  envoye: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  accepte: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  refuse: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  expire: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

function calcMontantHT(lignes: any[], remiseGlobale: string | null): number {
  const sousTotal = lignes.reduce((sum, l) => {
    const prix = Number(l.puHt) || 0;
    const qty = Number(l.quantite) || 1;
    const remise = Number(l.remise) || 0;
    const periods = l.typeLigne === 'location' ? (Number(l.nbPeriodes) || 1) : 1;
    return sum + prix * qty * periods * (1 - remise / 100);
  }, 0);
  const rg = Number(remiseGlobale) || 0;
  return sousTotal * (1 - rg / 100);
}

function formatPrix(n: number) {
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function DevisListPage() {
  const { navigate } = usePortailRouter();
  const { brand } = usePortailAuth();
  const primary = brand?.couleurPrimaire || "#2563eb";

  const [devis, setDevis] = useState<DevisItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authFetch("/api/portail/devis")
      .then(r => r.ok ? r.json() : Promise.reject(new Error("Erreur chargement devis")))
      .then(data => { if (!cancelled) setDevis(data); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-6 pb-4 border-b border-border/60 bg-card flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-foreground mb-0.5">Mes devis</h1>
          <p className="text-sm text-muted-foreground">Gérez vos demandes de devis auprès de l'équipe Jobotto</p>
        </div>
        <button
          onClick={() => navigate("/devis/nouveau")}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90 shrink-0"
          style={{ backgroundColor: primary }}
          data-testid="button-nouveau-devis"
        >
          <Plus className="w-4 h-4" />
          Nouveau devis
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-24 text-destructive">{error}</div>
        ) : devis.length === 0 ? (
          <div className="text-center py-24 space-y-3">
            <FileSignature className="w-12 h-12 text-muted-foreground/30 mx-auto" />
            <p className="text-muted-foreground">Aucun devis pour le moment</p>
            <button
              onClick={() => navigate("/devis/nouveau")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: primary }}
            >
              <Plus className="w-4 h-4" /> Créer mon premier devis
            </button>
          </div>
        ) : (
          <div className="bg-card border border-border/50 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/30">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Numéro</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Client final</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Produits</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Montant HT</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Statut</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {devis.map(d => {
                  const montantHT = calcMontantHT(d.lignes || [], d.remiseGlobale);
                  return (
                    <tr key={d.id} className="hover:bg-muted/20 transition-colors" data-testid={`row-devis-${d.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold text-primary">{d.numero}</span>
                          {d.typeDemande && d.typeDemande !== 'achat' && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${TYPE_LABELS[d.typeDemande]?.className || ''}`}>
                              {TYPE_LABELS[d.typeDemande]?.label || d.typeDemande}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{formatDate(d.createdAt)}</td>
                      <td className="px-4 py-3 text-foreground">
                        <div>{d.clientFinalNom || d.objet || "—"}</div>
                        {d.environnement && <div className="text-xs text-muted-foreground">{d.environnement}</div>}
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        <span className="flex items-center justify-end gap-1">
                          <Package className="w-3.5 h-3.5" />
                          {(d.lignes || []).length}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums">
                        {montantHT > 0 ? formatPrix(montantHT) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUT_CLASSES[d.statut] || "bg-muted text-muted-foreground"}`}>
                          {STATUT_LABELS[d.statut] || d.statut}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {d.statut === "brouillon" ? (
                            <button
                              onClick={() => navigate(`/devis/${d.id}/editer`)}
                              className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-border text-foreground hover:bg-secondary/60 transition-colors"
                              data-testid={`button-continuer-${d.id}`}
                            >
                              <Pencil className="w-3 h-3" /> Continuer
                            </button>
                          ) : (
                            <button
                              onClick={() => navigate(`/devis/${d.id}`)}
                              className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-border text-foreground hover:bg-secondary/60 transition-colors"
                              data-testid={`button-voir-${d.id}`}
                            >
                              <Eye className="w-3 h-3" /> Voir
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
